import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

declare global {
  interface Window {
    __MB_WS_DEBUG__?: {
      forceClose: () => void;
    };
  }
}

const WS_PATH = '/api/v1/ws';
const WS_DEBUG = process.env.NODE_ENV !== 'production';
const debugLog = (...args: unknown[]) => {
  if (WS_DEBUG) {
    console.log(...args);
  }
};

const normalizeWsEndpoint = (rawUrl: string): string => {
  const trimmed = rawUrl.trim();
  if (!trimmed) return trimmed;
  if (trimmed.includes(WS_PATH)) return trimmed;
  return `${trimmed.replace(/\/+$/, '')}${WS_PATH}`;
};

interface WebSocketContextType {
  socket: WebSocket | null;
  isConnected: boolean;
  sendMessage: (message: any) => void;
  lastError: string | null;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

// Функция за проверка на валидността на токена
const isTokenValid = (token: string): boolean => {
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      console.error('Invalid token format');
      return false;
    }

    const payload = JSON.parse(atob(tokenParts[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    
    if (Date.now() >= expirationTime) {
      console.error('Token has expired');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error parsing token:', error);
    return false;
  }
};

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const { token } = useAuth();
  const socketRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageTimeRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 5000;
  const MAX_MESSAGE_AGE = 60000; // 1 минута

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (socketRef.current) {
      const currentSocket = socketRef.current;
      
      // Премахваме всички слушатели преди да затворим връзката
      currentSocket.onopen = null;
      currentSocket.onmessage = null;
      currentSocket.onerror = null;
      currentSocket.onclose = null;
      
      // В dev StrictMode React прави бърз mount/unmount цикъл.
      // Ако затворим CONNECTING сокет тук, браузърът често логва
      // "closed before the connection is established" като шум.
      // Затваряме само вече отворени сокети, а за CONNECTING
      // оставяме връзката да се самоизчисти без слушатели.
      if (currentSocket.readyState === WebSocket.OPEN) {
        try {
          currentSocket.close();
        } catch (error) {
          console.error('Error closing WebSocket:', error);
        }
      }
      
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }
    
    isConnectingRef.current = false;
  }, []);

  const setupWebSocket = useCallback(() => {
    if (!token || !isTokenValid(token)) {
      debugLog('No valid token available');
      cleanup();
      return;
    }

    if (isConnectingRef.current || (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING)) {
      debugLog('Connection attempt already in progress');
      return;
    }

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      debugLog('WebSocket connection already established');
      return;
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      debugLog('Maximum reconnection attempts reached');
      setLastError('Maximum reconnection attempts reached');
      return;
    }

    cleanup();
    isConnectingRef.current = true;
    reconnectAttemptsRef.current++;

    try {
      const wsEndpoint = (() => {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const { hostname, port } = window.location;
        const isLocalDev = (hostname === 'localhost' || hostname === '127.0.0.1') && port === '3000';
        if (isLocalDev) {
          if (process.env.REACT_APP_RESPECT_LOCAL_API_URL === 'true' && process.env.REACT_APP_WS_URL) {
            return normalizeWsEndpoint(process.env.REACT_APP_WS_URL);
          }
          return `${protocol}//${hostname}:8000${WS_PATH}`;
        }

        if (process.env.REACT_APP_WS_URL) {
          return normalizeWsEndpoint(process.env.REACT_APP_WS_URL);
        }

        const portPart = port ? `:${port}` : '';
        return `${protocol}//${hostname}${portPart}${WS_PATH}`;
      })();
      
      debugLog('Attempting to establish WebSocket connection...', wsEndpoint);
      const ws = new WebSocket(wsEndpoint);
      socketRef.current = ws;

      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          debugLog('WebSocket connection attempt timed out');
          cleanup();
          
          // Опитваме се да се свържем отново след забавяне
          if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectTimeoutRef.current = setTimeout(setupWebSocket, RECONNECT_DELAY);
          }
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        debugLog('WebSocket connection established, sending auth message');
        
        // Изпращаме токена веднага след свързване
        ws.send(JSON.stringify({ type: 'auth', token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          lastMessageTimeRef.current = Date.now();
          
          if (data.type === 'connection_established') {
            debugLog('WebSocket authentication successful');
            setSocket(ws);
            setIsConnected(true);
            setLastError(null);
            isConnectingRef.current = false;
            reconnectAttemptsRef.current = 0;

            // Стартираме heartbeat след успешна аутентикация
            heartbeatIntervalRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                // Проверяваме кога е било последното съобщение
                const lastMessageTime = lastMessageTimeRef.current;
                if (lastMessageTime && Date.now() - lastMessageTime > MAX_MESSAGE_AGE) {
                  debugLog('No messages received for too long, reconnecting...');
                  cleanup();
                  setupWebSocket();
                  return;
                }
                
                ws.send(JSON.stringify({ type: 'ping' }));
              }
            }, 30000);
          }
          else if (data.type === 'auth_error') {
            console.error('WebSocket authentication failed:', data.message);
            setLastError(data.message || 'Authentication failed');
            cleanup();
          }
          else if (data.type === 'notification') {
            debugLog('Notification received:', data.payload);
          }
          else if (data.type === 'pong') {
            debugLog('Received pong from server');
            lastMessageTimeRef.current = Date.now();
          }
        } catch (error) {
          console.error('Error processing message:', error);
        }
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket error occurred:', error);
        setLastError('WebSocket connection error');
        cleanup();
        
        // Опитваме се да се свържем отново след забавяне
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectTimeoutRef.current = setTimeout(setupWebSocket, RECONNECT_DELAY);
        }
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        debugLog('WebSocket connection closed:', event.code, event.reason);
        // Code 1000 = normal closure (common in dev StrictMode remounts).
        const isNormalClosure = event.code === 1000;
        if (!isNormalClosure) {
          setLastError('WebSocket connection closed');
        } else {
          setLastError(null);
        }
        cleanup();
        
        // Опитваме се да се свържем отново след забавяне
        if (!isNormalClosure && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectTimeoutRef.current = setTimeout(setupWebSocket, RECONNECT_DELAY);
        }
      };

    } catch (error) {
      console.error('Error setting up WebSocket:', error);
      setLastError('Error setting up WebSocket connection');
      cleanup();
      
      // Опитваме се да се свържем отново след забавяне
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectTimeoutRef.current = setTimeout(setupWebSocket, RECONNECT_DELAY);
      }
    }
  }, [token, cleanup]);

  const sendMessage = useCallback((message: any) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      setLastError('WebSocket is not connected');
      
      // Опитваме се да се свържем отново и да изпратим съобщението
      cleanup();
      setupWebSocket();
      
      // Запазваме съобщението за повторно изпращане след свързване
      const retryInterval = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          try {
            socketRef.current.send(JSON.stringify(message));
            clearInterval(retryInterval);
          } catch (error) {
            console.error('Error sending message:', error);
            setLastError('Error sending message');
          }
        }
      }, 1000);
      
      // Спираме опитите след 5 секунди
      setTimeout(() => {
        clearInterval(retryInterval);
      }, 5000);
      
      return;
    }

    try {
      socketRef.current.send(JSON.stringify(message));
    } catch (error) {
      console.error('Error sending message:', error);
      setLastError('Error sending message');
      
      // Ако има грешка при изпращане, опитваме се да рестартираме връзката
      cleanup();
      setupWebSocket();
    }
  }, [cleanup, setupWebSocket]);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    setupWebSocket();
  }, [setupWebSocket]);

  useEffect(() => {
    if (token) {
      debugLog('WebSocketContext: Token available, checking validity');
      if (isTokenValid(token)) {
        debugLog('WebSocketContext: Token is valid, setting up WebSocket');
        setupWebSocket();
      } else {
        debugLog('WebSocketContext: Token is invalid, cleaning up');
        cleanup();
      }
    } else {
      debugLog('WebSocketContext: No token, cleaning up WebSocket');
      cleanup();
    }
    
    return () => {
      debugLog('WebSocketContext: Component unmounting, cleaning up');
      cleanup();
    };
  }, [token, setupWebSocket, cleanup]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      window.__MB_WS_DEBUG__ = {
        forceClose: () => {
          if (socketRef.current) {
            socketRef.current.close();
          }
        },
      };
    }

    return () => {
      if (window.__MB_WS_DEBUG__) {
        delete window.__MB_WS_DEBUG__;
      }
    };
  }, []);

  const value = {
    socket,
    isConnected,
    sendMessage,
    lastError,
    reconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}; 